/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {
  Alert,
  Button,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  type FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  getManualStatusTargets,
  isDefiniteNewVehicleRejection,
  isFactoryReferenceInUse,
  isFactoryReferenceLocked,
  isNewVehicleConflict,
  type VehicleOrderDetailResponseBody,
  useCancelOrderMutation,
  useChangeFactoryReferenceMutation,
  useGetVehicleOrderQuery,
  useHandoverOrderMutation,
  useRecordOrderArrivalMutation,
  useUpdateOrderStatusMutation,
} from '@dms/api/new-vehicle';
import {type ApiException, isApiException} from '@dms/api/utils';
import {
  FactoryReferenceOwnerLink,
  getFactoryReferenceInUseMessage,
  getNewVehicleEnumLabel,
  importerOrderRefSchema,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {type ApiError, isFetchBaseQueryError} from 'shared/web';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});

const INVALID_ARRIVAL_VIN_CODE = 'DMS-NV-VO-0014';
const NEW_VEHICLE_DOMAIN_ERROR_PREFIX = 'DMS-NV-';

// RTK Query rejects with a FetchBaseQueryError or a SerializedError (whose fields are all optional),
// so this only filters out nullish and primitive throws from the success callback; handleApiError
// re-checks the shape of any object it receives.
const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && isNotNil(error);

// Coded 4xx order rejections are expected outcomes: show the backend's reason in the dialog.
const isDomainError = (item: unknown): item is ApiException['errors'][number] =>
  typeof item === 'object' &&
  isNotNil(item) &&
  'code' in item &&
  typeof item.code === 'string' &&
  item.code.startsWith(NEW_VEHICLE_DOMAIN_ERROR_PREFIX) &&
  'message' in item &&
  typeof item.message === 'string' &&
  item.message.length > 0;

const getDomainError = (error: unknown) => {
  const errors: unknown =
    isApiError(error) &&
    isFetchBaseQueryError(error) &&
    !isNewVehicleConflict(error) &&
    isDefiniteNewVehicleRejection(error) &&
    isApiException(error.data)
      ? error.data.errors
      : undefined;
  return Array.isArray(errors) ? errors.find(isDomainError) : undefined;
};

function useMutationFailure(refetch: VoidFunction) {
  const [conflict, setConflict] = useState(false);
  const handleFailure = (error: unknown) =>
    match(error)
      .when(isNewVehicleConflict, () => {
        setConflict(true);
        refetch();
      })
      // Domain errors render from the mutation state, see FailureAlerts.
      .when(
        (failure) => isNotNil(getDomainError(failure)),
        () => undefined
      )
      .when(isApiError, (failure) => handleApiError(failure))
      .otherwise(() => showNotification.error());
  return {conflict, handleFailure};
}

function FailureAlerts(props: {conflict: boolean; domainError?: string}) {
  return (
    <>
      <ConflictAlert show={props.conflict} />
      <Show when={isNotNilOrEmpty(props.domainError) ? props.domainError : null}>
        {(domainError) => <Alert variant="error" message={domainError} />}
      </Show>
    </>
  );
}

function ConflictAlert(props: {show: boolean}) {
  return (
    <Show when={props.show}>
      <Alert
        variant="warning"
        message={t(
          'entity.newVehicle.order.orderConflict',
          'This order changed. Review the refreshed details and try again.'
        )}
      />
    </Show>
  );
}

const statusSchema = z.object({
  status: z.enum(['ordered', 'in_production', 'in_transit', 'ready_for_handover']),
  note: z.string(),
});
type StatusValues = z.infer<typeof statusSchema>;
const statusValues = statusSchema.shape.status.options;

export function UpdateStatusDialog(props: {order: VehicleOrderDetailResponseBody}) {
  const orderQuery = useGetVehicleOrderQuery({orderId: props.order.id});
  const order = orderQuery.currentData ?? props.order;
  const [update, mutation] = useUpdateOrderStatusMutation();
  const failure = useMutationFailure(orderQuery.refetch);
  // The refetch after a stale version remounts the form; the seller's entries survive it.
  const [draft, setDraft] = useState<StatusValues>();
  const targets = getManualStatusTargets(order);
  const options = statusValues
    .filter((value) => targets.includes(value))
    .map((value) => ({value, label: getNewVehicleEnumLabel('orderStatus', value, t)}));
  const submit = (values: StatusValues) =>
    update({
      orderId: order.id,
      updateOrderStatusRequestBody: {
        status: values.status,
        note: values.note || null,
        version: order.version,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(
          t('entity.newVehicle.notifications.statusUpdated', 'Order status updated')
        );
      })
      .catch(failure.handleFailure);

  return (
    <VStack spacing={4}>
      <FailureAlerts
        conflict={failure.conflict}
        domainError={getDomainError(mutation.error)?.message}
      />
      <Form
        key={order.version}
        experimentalZodSchema={statusSchema}
        defaultValues={{
          status:
            isNotNil(draft) && targets.includes(draft.status)
              ? draft.status
              : (options[0]?.value ?? 'ordered'),
          note: draft?.note ?? '',
        }}
        onChange={(values) => setDraft(values)}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="status"
              type="choice"
              label={t('entity.newVehicle.external.stage', 'Delivery stage')}
              options={options}
              isNotClearable
              menuInPortal
            />
            <FormField
              control={control}
              name="note"
              type="textarea"
              label={i18n.t('general.labels.note')}
            />
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="submit"
                title={t('entity.newVehicle.actions.updateStatus', 'Update status')}
                isLoading={mutation.isLoading}
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}

const arrivalSchema = z.object({vin: z.string(), note: z.string()});
type ArrivalValues = z.infer<typeof arrivalSchema>;

export function RecordArrivalDialog(props: {
  order: VehicleOrderDetailResponseBody;
  vehicleVin?: string | null;
}) {
  const orderQuery = useGetVehicleOrderQuery({orderId: props.order.id});
  const order = orderQuery.currentData ?? props.order;
  const [recordArrival, mutation] = useRecordOrderArrivalMutation();
  const failure = useMutationFailure(orderQuery.refetch);
  const domainError = getDomainError(mutation.error);
  const submit: FormSubmitHandler<ArrivalValues> = (values, setErrors) =>
    recordArrival({
      orderId: order.id,
      arrivalRequestBody: {
        vin: values.vin || null,
        note: values.note || null,
        version: order.version,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(
          t('entity.newVehicle.notifications.arrivalRecorded', 'Vehicle arrival recorded')
        );
      })
      .catch((error: unknown) => {
        const vinError = getDomainError(error);
        if (vinError?.code === INVALID_ARRIVAL_VIN_CODE) {
          setErrors([{name: 'vin', message: vinError.message}]);
          return;
        }
        failure.handleFailure(error);
      });

  return (
    <VStack spacing={4}>
      <FailureAlerts
        conflict={failure.conflict}
        domainError={
          domainError?.code === INVALID_ARRIVAL_VIN_CODE ? undefined : domainError?.message
        }
      />
      <Form
        experimentalZodSchema={arrivalSchema}
        defaultValues={{vin: props.vehicleVin ?? '', note: ''}}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="vin"
              type="text"
              label={i18n.t('entity.vehicle.labels.vin')}
            />
            <FormField
              control={control}
              name="note"
              type="textarea"
              label={i18n.t('general.labels.note')}
            />
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="submit"
                title={t('entity.newVehicle.actions.recordArrival', 'Record arrival')}
                isLoading={mutation.isLoading}
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}

const cancelSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, t('entity.newVehicle.order.reasonRequired', 'Reason is required')),
});
type CancelValues = z.infer<typeof cancelSchema>;

export function CancelOrderDialog(props: {order: VehicleOrderDetailResponseBody}) {
  const orderQuery = useGetVehicleOrderQuery({orderId: props.order.id});
  const order = orderQuery.currentData ?? props.order;
  const [cancel, mutation] = useCancelOrderMutation();
  const failure = useMutationFailure(orderQuery.refetch);
  const submit = (values: CancelValues) =>
    cancel({
      orderId: order.id,
      cancelOrderRequestBody: {reason: values.reason, version: order.version},
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(
          t('entity.newVehicle.notifications.orderCancelled', 'Order cancelled')
        );
      })
      .catch(failure.handleFailure);

  return (
    <VStack spacing={4}>
      <FailureAlerts
        conflict={failure.conflict}
        domainError={getDomainError(mutation.error)?.message}
      />
      <Form experimentalZodSchema={cancelSchema} defaultValues={{reason: ''}} onSubmit={submit}>
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="reason"
              type="textarea"
              label={t('entity.newVehicle.order.cancellationReason', 'Cancellation reason')}
              isRequired
            />
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="submit"
                title={t('entity.newVehicle.actions.cancelOrder', 'Cancel order')}
                isLoading={mutation.isLoading}
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}

export function HandoverOrderDialog(props: {order: VehicleOrderDetailResponseBody}) {
  const orderQuery = useGetVehicleOrderQuery({orderId: props.order.id});
  const order = orderQuery.currentData ?? props.order;
  const [handover, mutation] = useHandoverOrderMutation();
  const failure = useMutationFailure(orderQuery.refetch);
  const submit = () =>
    handover({
      orderId: order.id,
      handoverRequestBody: {version: order.version},
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(
          t('entity.newVehicle.notifications.orderHandedOver', 'Vehicle handed over')
        );
      })
      .catch(failure.handleFailure);

  return (
    <VStack spacing={4}>
      <FailureAlerts
        conflict={failure.conflict}
        domainError={getDomainError(mutation.error)?.message}
      />
      <Text>
        {t(
          'entity.newVehicle.order.handoverConfirmation',
          'Confirm that the vehicle has been handed over to the customer.'
        )}
      </Text>
      <HStack justify="flex-end">
        <Button
          variant="primary"
          title={t('entity.newVehicle.actions.handOver', 'Hand over')}
          isLoading={mutation.isLoading}
          onClick={submit}
        />
      </HStack>
    </VStack>
  );
}

const factoryReferenceSchema = z.object({importerOrderRef: importerOrderRefSchema});
type FactoryReferenceValues = z.infer<typeof factoryReferenceSchema>;

export function ChangeFactoryReferenceDialog(props: {order: VehicleOrderDetailResponseBody}) {
  const orderQuery = useGetVehicleOrderQuery({orderId: props.order.id});
  const order = orderQuery.currentData ?? props.order;
  const [changeReference, mutation] = useChangeFactoryReferenceMutation();
  const failure = useMutationFailure(orderQuery.refetch);
  // The owner holds the reference of the order version it was refused on.
  const [owner, setOwner] = useState<{vehicleId: string; version: number}>();
  const ownerVehicleId = owner?.version === order.version ? owner.vehicleId : undefined;
  // Outside the form: the refetch after a lock bumps the version, which remounts the form.
  const [isLocked, setIsLocked] = useState(false);
  // Survives that remount, so the seller does not retype the reference after a stale version.
  const [draft, setDraft] = useState<FactoryReferenceValues>();
  const submit: FormSubmitHandler<FactoryReferenceValues> = (values, setErrors) => {
    setOwner(undefined);
    setIsLocked(false);
    return changeReference({
      orderId: order.id,
      changeFactoryReferenceRequestBody: {
        importerOrderRef: values.importerOrderRef.trim() || null,
        version: order.version,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
        showNotification.success(i18n.t('general.notifications.changesSaved'));
      })
      .catch((error: unknown) =>
        match(error)
          .when(isFactoryReferenceInUse, (inUse) => {
            setOwner({vehicleId: inUse.data.vehicleId, version: order.version});
            setErrors([
              {
                name: 'importerOrderRef',
                message: getFactoryReferenceInUseMessage(),
              },
            ]);
          })
          .when(isFactoryReferenceLocked, () => {
            setIsLocked(true);
            void orderQuery.refetch();
          })
          .otherwise(failure.handleFailure)
      );
  };

  return (
    <VStack spacing={4}>
      <FailureAlerts
        conflict={failure.conflict}
        domainError={getDomainError(mutation.error)?.message}
      />
      <Show when={isLocked}>
        <Alert
          variant="error"
          message={t(
            'entity.newVehicle.order.factoryReferenceLocked',
            'The reference can no longer be changed because the vehicle was handed over.'
          )}
        />
      </Show>
      <Form
        key={order.version}
        experimentalZodSchema={factoryReferenceSchema}
        defaultValues={{importerOrderRef: draft?.importerOrderRef ?? order.importerOrderRef ?? ''}}
        onChange={(values) => setDraft(values)}
        onSubmit={submit}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="importerOrderRef"
              type="text"
              label={t('entity.newVehicle.order.importerReference', 'Importer order reference')}
              onChange={() => setOwner(undefined)}
            />
            <Show when={ownerVehicleId}>
              {(vehicleId) => <FactoryReferenceOwnerLink vehicleId={vehicleId} />}
            </Show>
            <HStack justify="flex-end">
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.save')}
                isDisabled={isLocked}
                isLoading={mutation.isLoading}
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </VStack>
  );
}
